import React, { useState } from "react";
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Switch,
    Textarea,
    VStack,
    useToast,
    HStack,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";

// Services
import { exerciseService, CreateExerciseParams } from "services/exerciseService";

interface CreateExerciseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export default function CreateExerciseModal({
    isOpen,
    onClose,
    onSuccess,
}: CreateExerciseModalProps) {
    const [formData, setFormData] = useState<CreateExerciseParams>({
        name: "",
        description: "",
        category: "",
        muscle_group: "",
        equipment: "",
        instructions: "",
        image_url: "",
        is_public: false,
    });
    const [isLoading, setIsLoading] = useState(false);

    const toast = useToast();

    // Chakra Color Mode
    const bgColor = useColorModeValue("white", "navy.800");
    const textColor = useColorModeValue("navy.700", "white");
    const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const inputBg = useColorModeValue("white", "navy.700");

    // Exercise options
    const categories = [
        "strength",
        "cardio",
        "flexibility",
        "sports",
        "balance",
        "coordination"
    ];

    const muscleGroups = [
        "chest",
        "back",
        "shoulders",
        "arms",
        "legs",
        "core",
        "full body",
        "glutes"
    ];

    const equipment = [
        "bodyweight",
        "dumbbells",
        "barbell",
        "resistance bands",
        "machines",
        "kettlebells",
        "cables",
        "none"
    ];

    const handleInputChange = (field: keyof CreateExerciseParams, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            // Validation
            if (!formData.name.trim()) {
                toast({
                    title: "Error",
                    description: "Exercise name is required",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            if (!formData.category) {
                toast({
                    title: "Error",
                    description: "Please select a category",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            if (!formData.muscle_group) {
                toast({
                    title: "Error",
                    description: "Please select a muscle group",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            if (!formData.equipment) {
                toast({
                    title: "Error",
                    description: "Please select equipment",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            await exerciseService.createExercise(formData);
            onSuccess();
            handleClose();
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to create exercise",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: "",
            description: "",
            category: "",
            muscle_group: "",
            equipment: "",
            instructions: "",
            image_url: "",
            is_public: false,
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent bg={bgColor} borderRadius="20px" border="1px solid" borderColor={borderColor}>
                <ModalHeader color={textColor} fontWeight="700" fontSize="22px">
                    Create New Exercise
                </ModalHeader>
                <ModalCloseButton color={textColor} />
                <ModalBody>
                    <VStack spacing={5}>
                        {/* Basic Information */}
                        <FormControl isRequired>
                            <FormLabel color={textColor} fontWeight="500">
                                Exercise Name
                            </FormLabel>
                            <Input
                                placeholder="Enter exercise name"
                                value={formData.name}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Description
                            </FormLabel>
                            <Textarea
                                placeholder="Brief description of the exercise"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                rows={3}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Categories */}
                        <HStack spacing={5} w="100%">
                            <FormControl isRequired>
                                <FormLabel color={textColor} fontWeight="500">
                                    Category
                                </FormLabel>
                                <Select
                                    placeholder="Select category"
                                    value={formData.category}
                                    onChange={(e) => handleInputChange("category", e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    color={textColor}
                                    _focus={{
                                        borderColor: "brand.500",
                                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                    }}
                                >
                                    {categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color={textColor} fontWeight="500">
                                    Muscle Group
                                </FormLabel>
                                <Select
                                    placeholder="Select muscle group"
                                    value={formData.muscle_group}
                                    onChange={(e) => handleInputChange("muscle_group", e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    color={textColor}
                                    _focus={{
                                        borderColor: "brand.500",
                                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                    }}
                                >
                                    {muscleGroups.map((muscle) => (
                                        <option key={muscle} value={muscle}>
                                            {muscle.charAt(0).toUpperCase() + muscle.slice(1)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </HStack>

                        <FormControl isRequired>
                            <FormLabel color={textColor} fontWeight="500">
                                Equipment
                            </FormLabel>
                            <Select
                                placeholder="Select equipment"
                                value={formData.equipment}
                                onChange={(e) => handleInputChange("equipment", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            >
                                {equipment.map((equip) => (
                                    <option key={equip} value={equip}>
                                        {equip.charAt(0).toUpperCase() + equip.slice(1)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Instructions */}
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Instructions
                            </FormLabel>
                            <Textarea
                                placeholder="Step-by-step instructions for performing the exercise"
                                value={formData.instructions}
                                onChange={(e) => handleInputChange("instructions", e.target.value)}
                                rows={4}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Image URL */}
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="500">
                                Image URL (Optional)
                            </FormLabel>
                            <Input
                                placeholder="https://example.com/exercise-image.jpg"
                                value={formData.image_url}
                                onChange={(e) => handleInputChange("image_url", e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: secondaryTextColor }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)"
                                }}
                            />
                        </FormControl>

                        {/* Public Toggle */}
                        <FormControl>
                            <HStack justify="space-between" w="100%">
                                <VStack align="flex-start" spacing={0}>
                                    <FormLabel color={textColor} fontWeight="500" mb={0}>
                                        Make Public
                                    </FormLabel>
                                    <Text fontSize="14px" color={secondaryTextColor}>
                                        Allow other trainers to use this exercise
                                    </Text>
                                </VStack>
                                <Switch
                                    isChecked={formData.is_public}
                                    onChange={(e) => handleInputChange("is_public", e.target.checked)}
                                    colorScheme="brand"
                                />
                            </HStack>
                        </FormControl>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button 
                        mr={3} 
                        onClick={handleClose}
                        variant="ghost"
                        color={textColor}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        loadingText="Creating..."
                        colorScheme="brand"
                    >
                        Create Exercise
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
